/* eslint-disable no-mixed-spaces-and-tabs */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Button, Stack, Typography } from '@mui/material';
import useSWR from 'swr';
import { useDispatch } from 'react-redux';
import { setLastCommunicationDate } from '@store/reducers/contactManagerSlice';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { dataGridCol } from '@shared/components/grid/common';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { formatDate, formatPhoneNumber, formatSecondsToMinutesAndSeconds, gridDateTimeFormatter, queryParams } from '@shared/commonFunctions';
import { dateFormatWithTime } from '@shared/constants/formats';
// import { OneToOneConsentPresentedAndAccepted } from '@shared/constants/constants';
import { IConfiguration } from '@modules/admin/AdminTools/ConfigurationTable/types';
import { SERVICES } from '@shared/constants/apiConfig';
import { getMappedContactConsentOptOutData } from '../../shared';
import { useGetConsentQuery } from '../Contact/queries';
import { StyleOverride } from './StyleOverride';
import { LeadDetailsHeader } from './components/LeadsTabComponents';
import { IDialerData, ILeadNote, ILeadsCommunicationProps } from './types/interfaces/interfaces';
import { CommunicationModal } from './components/CommunicationModal/CommunicationModal';
import { requestAddCommunication, requestDialerCallData, requestSendOneClick } from './requests';
import { CommunicateFormType, NoteTypeEnum, NoteTypeEnumDescriptions } from './components/CommunicationModal/types';

const TELEPHONE_CONSUMER_PROTECTION_ACT_CONFIGURATION_GROUP = 'tcpa';
const TELEPHONE_CONSUMER_PROTECTION_ACT_TOKEN_NAME ='states';

const detailsColumns: GridColDef[] = [
  dataGridCol('createdDate', {
    headerName: 'Date',
    flex: 2,
    minWidth: 100,
    valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
  }),
  dataGridCol('leadNoteTypes', {
    headerName: 'Type',
    flex: 1,
    minWidth: 100,
    valueGetter: ({ row }) => {
      const noteType = NoteTypeEnum[row.leadNoteTypes[0] as keyof typeof NoteTypeEnum];
      return NoteTypeEnumDescriptions[noteType];
    },
  }),
  dataGridCol('note', {
    headerName: 'Notes',
    flex: 5,
    minWidth: 100,
  }),
  dataGridCol('createdBy', {
    headerName: 'Entered By',
    flex: 1.5,
    minWidth: 100,
    renderCell: ({ row }) => row?.createdBy?.name ?? '',
  }),
];

const callDataColumns: GridColDef[] = [
  dataGridCol('date', {
    headerName: 'Call Start Time',
    flex: 2,
    minWidth: 175,
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('type', {
    headerName: 'Type',
    flex: 2,
    minWidth: 50,
  }),
  dataGridCol('result', {
    headerName: 'Result',
    flex: 7,
    minWidth: 125,
  }),
  dataGridCol('duration', {
    headerName: 'Duration',
    flex: 5,
    minWidth: 100,
    valueGetter: ({ value }) => formatSecondsToMinutesAndSeconds(value)
  }),
  dataGridCol('numberDialed', {
    headerName: 'Number Dialed',
    flex: 5,
    minWidth: 75,
    valueGetter: ({ value }) => formatPhoneNumber(value)
  }),
  dataGridCol('agent', {
    headerName: 'Agent',
    flex: 5,
    minWidth: 75,
  }),
];

export const LeadsCommunicationTable = ({ lead, mutateLeads, contactDetails }: ILeadsCommunicationProps) => {
  const [TCPAStates, setTCPAStates] = useState<string[] | null>(null);
  const { data: configuration, isValidating: isLoadingConfiguration } = useSWR<IConfiguration[]>(`${SERVICES.CONFIGURATION}/configurations/all`);

  const params = queryParams();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [callData, setCallData] = useState<IDialerData[] | any>([]);
  const [disableOneClick, setDisableOneClick] = useState(false);

  const { data: notes, mutate, isValidating: isLoading } = useSWR<ILeadNote[]>(lead && `/lead-service/lead/${lead.id}/notes`);
  const { consentData } = useGetConsentQuery(contactDetails);
  const [communicationModalOpenStatus, handleCommunicationModalOpenStatus] = useState<boolean>(false);

  useEffect(() => {
    if (configuration && configuration.length > 0 && !isLoadingConfiguration) {
      const TCPAConfigurationValues = configuration
                              .find((item) => item.configurationGroup.trim().toLowerCase() === TELEPHONE_CONSUMER_PROTECTION_ACT_CONFIGURATION_GROUP &&
                                              item.tokenName.trim().toLowerCase() === TELEPHONE_CONSUMER_PROTECTION_ACT_TOKEN_NAME)
                              ?.value;  
      if (TCPAConfigurationValues && TCPAConfigurationValues.length > 0) {
        const TCPAstates = TCPAConfigurationValues.split(',').map(v => v.trim().toLowerCase());
        setTCPAStates(TCPAstates);
      }
    }
  }, [configuration, isLoadingConfiguration]);


  const sortedCalls = useMemo(() => {
      const sortedLeads = callData?.sort((a: { date: string; }, b: { date: string; }) => b.date.localeCompare(a.date));
  
      return sortedLeads;
    }, [callData]);

  const phoneNumbersArr = useMemo(() => {
    const phoneNumbers = [];
    if (contactDetails?.cellPhone) {
      phoneNumbers.push(contactDetails.cellPhone);
    }
    if (contactDetails?.homePhone) {
      phoneNumbers.push(contactDetails.homePhone);
    }
    if (contactDetails?.workPhone) {
      phoneNumbers.push(contactDetails.workPhone);
    }
    return phoneNumbers;
  }, [contactDetails?.cellPhone, contactDetails?.homePhone ,contactDetails?.workPhone]);
  
  useEffect(() => {
    if (lead?.contactId && phoneNumbersArr.length) {
      setLoading(true);
      requestDialerCallData(phoneNumbersArr).then((res) => {
        setCallData(res);
      }).finally(() => {
        setLoading(false);
      });
    }
  }, [lead.contactId, phoneNumbersArr]);

  useEffect(() => {
    try {
      if (!TCPAStates ||
          !contactDetails ||
          !contactDetails.state ||
          (TCPAStates && TCPAStates.length > 0 && contactDetails.state && TCPAStates.includes(contactDetails.state.trim().toLowerCase()))) {
        setDisableOneClick(true);
      } else {
        setDisableOneClick(false);
      }
    } catch (error) {
      setDisableOneClick(true);
    }
  }, [contactDetails, TCPAStates]);

  const handleCommunicationSubmit = (data: CommunicateFormType) => {
    setLoading(true);
    requestAddCommunication({
      contactId: params?.id || '',
      leadId: lead?.id || '',
      leadNoteTypes: [data.type],
      note: data.notes,
    }).then(() => {
      dispatch(setLastCommunicationDate(new Date()));
      setLoading(false);
      handleCommunicationModalOpenStatus(false);
      mutate();
      //  TODO - Instead of reloading all leads, it takes around 800ms
      //  it may be faster to just increase/update #Comms and 'Last Comm date' data in leads object
      mutateLeads();
    });
  };

  const handleSendOneClickButton = useCallback(async () => {
    const contactConsentOptOut = getMappedContactConsentOptOutData(consentData ?? []);
    if (!contactConsentOptOut.checkContactEmail && !contactConsentOptOut.checkContactText) {
      Notistack.generalError('One click SMS and Email not sent. Borrower is Opted out.');
    } else {
      await requestSendOneClick(params?.id, lead?.id);
      dispatch(setLastCommunicationDate(new Date()));
    }
  }, [params?.id, lead?.id, consentData]);

  return (
    <div>
      <StyleOverride />

      <LeadDetailsHeader>
        <Typography variant="h3">{`Lead ${lead.numericId} Communications`}</Typography>

        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 2 }}
        >
          <Typography
            variant="subtitle1"
            onClick={() => handleCommunicationModalOpenStatus(true)}
          >
            Add Communication
          </Typography>

          <Button
            // TODO - use start date form config when OneToOne is enabled
            // disabled={!(lead.oneToOneConsentPresentedAndAccepted?.toString() == OneToOneConsentPresentedAndAccepted.Yes)}
            disabled={disableOneClick ?? true}
            variant="contained"
            onClick={handleSendOneClickButton}
          >
            Send One Click
          </Button>
        </Stack>
      </LeadDetailsHeader>

      <PhoenixGrid
        canNotCopy
        noButton
        autoHeight
        columns={detailsColumns}
        rows={notes || []}
        loading={isLoading}
        pagination={false}
      />

      <LeadDetailsHeader>
        <Typography variant="h3">Call Data</Typography>
      </LeadDetailsHeader>

      <PhoenixGrid
        canNotCopy
        noButton
        autoHeight
        columns={callDataColumns}
        rows={sortedCalls || []}
        loading={loading}
        pagination={true}
        pageSize={10}
      />

      {communicationModalOpenStatus && (
        <CommunicationModal
          loading={loading}
          handleClose={() => handleCommunicationModalOpenStatus(false)}
          handleSubmit={handleCommunicationSubmit}
        />
      )}
    </div>
  );
};
